import React, { FC, MutableRefObject, ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { RouterCheckbox } from '../../Atoms/Inputs/RouterCheckbox'
import { RoundedButton } from '../../Atoms/RoundedButton/RoundedButton'
import useIsMountedRef from 'use-is-mounted-ref'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { LanguageContext, ReactState } from '../../../../React/Common/Contexts'
import PromptContext, { PromptDisplayType } from '../../../../React/Common/Contexts/PromptContext'
import { Router as RouterLang, GenericAdminFilterLanguage, LicenseManagementLanguage } from '../../../../../i18n'
import { PageTitle } from '../../Molecules/PageTitle/PageTitle'
import { DataTable, DataTableColumn, DataTableSection } from '../../Molecules/DataTable/DataTable'
import IconButton, { IconButtonType } from '../../../../React/Common/Components/IconButton'
import { IconName } from '../../../../React/Common/Components/Icon'
import { displayError, displaySuccessMessage } from '../../../../React/Common/Utils/Messages'
import {
    getLicenseManagementReportDownloadURL,
    getLicenseManagementStatus,
    reloadLicenseManagementStatus
} from '../../../../React/Common/API/Licence/LicenceAPI'
import {
    LicenseManagementStatusResponse,
    LicenseStatusItem,
    LicenseStatusRow,
    LicenseTelSysProfile
} from '../../../../NonReact/Types/LicenseManagementTypes'
import { EMPTY_STRING } from '../../../../NonReact/constants/Common'
import { AdminFilters, AdminFilterValues } from '../../Molecules/AdminFilters/AdminFilters'
import { UserInfo, useUserInfo } from '../../../Hooks/useUserInfo'
import { Permissions } from '../../../../React/Common/Contexts/UserInfoContext'
import { hasPermission } from '../../../../React/Common/Utils/PermissionsUtil'
import { ALL_VALUE, ANY_VALUE_STRING_NUMERIC } from '../../../../React/Common/Constants/DefaultValues'
import { DEFAULT_CME_ID } from '../../../../React/Common/Constants/ServerValues'
import { IsVarEmpty, isStringNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'

const LICENSE_MANAGEMENT_PANEL_SECTION_CLASS = 'license-management-panel-section'
const LICENSE_MANAGEMENT_STATUS_TITLE_CLASS = 'license-management-status-title'
const LICENSE_REPORT_BILLING_SUMMARY = 'billingSummary' as const
const LICENSE_REPORT_BILLING_CDR = 'billingCdr' as const
const LICENSE_REPORT_DIAGNOSTIC = 'diagnostic' as const

type LicenseReportType =
    | typeof LICENSE_REPORT_BILLING_SUMMARY
    | typeof LICENSE_REPORT_BILLING_CDR
    | typeof LICENSE_REPORT_DIAGNOSTIC

const DEFAULT_REPORT_FILTERS: AdminFilterValues = {
    cme: null,
    telephonyServer: null,
    partition: null,
    crmType: null,
    scope: null,
    codeType: null,
    includeCmeChildren: null,
    search: undefined
}

/**
 * Maps one API status item into a table row.
 *
 * @param item - Status item returned by the backend.
 * @param key - Stable row key.
 * @returns Typed row used by the licence management tables.
 */
const mapStatusItemToRow = (item: LicenseStatusItem, key: string): LicenseStatusRow => ({
    key,
    Description: item.Description ?? EMPTY_STRING,
    value: item.Value ?? EMPTY_STRING,
    usage: item.Usage ?? EMPTY_STRING,
    statusMessage: item.StatusMessage ?? EMPTY_STRING
})

const isAllTelSysSelection = (value: Nullable<string | number>): boolean =>
    IsVarEmpty(value) ||
    value === ALL_VALUE ||
    String(value) === String(ALL_VALUE) ||
    String(value) === ANY_VALUE_STRING_NUMERIC

/**
 * Routed licence management page.
 *
 * @returns Licence management page content.
 */
export const LicenseManagement: FC = (): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(RouterLang.NAME)
    language.loadBundle(GenericAdminFilterLanguage.NAME)
    language.loadBundle(LicenseManagementLanguage.NAME)

    const { displayPromptPromise }: PromptDisplayType = useContext(PromptContext)
    const [userInfo]: UserInfo = useUserInfo()

    const [status, setStatus]: ReactState<Nullable<LicenseManagementStatusResponse>> =
        useState<Nullable<LicenseManagementStatusResponse>>(null)
    const [isLoading, setIsLoading]: ReactState<boolean> = useState<boolean>(true)
    const [isReloading, setIsReloading]: ReactState<boolean> = useState<boolean>(false)
    const [reportFilters, setReportFilters]: ReactState<AdminFilterValues> =
        useState<AdminFilterValues>(DEFAULT_REPORT_FILTERS)
    const [onlyBillableConfiguration, setOnlyBillableConfiguration]: ReactState<boolean> = useState<boolean>(false)
    const [includeSystemWideUsers, setIncludeSystemWideUsers]: ReactState<boolean> = useState<boolean>(false)
    const [includeBillingExcludedConfigurations, setIncludeBillingExcludedConfigurations]: ReactState<boolean> =
        useState<boolean>(false)
    const [includeSimulationTelephoneSystems, setIncludeSimulationTelephoneSystems]: ReactState<boolean> =
        useState<boolean>(false)

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const loadStatus = useCallback((): void => {
        setIsLoading(true)

        getLicenseManagementStatus()
            .then((response: Nullable<LicenseManagementStatusResponse>): void => {
                if (!isMounted.current) return

                setStatus(response)
            })
            .catch(displayError)
            .finally((): void => {
                if (isMounted.current) setIsLoading(false)
            })
    }, [])

    useEffect((): void => {
        loadStatus()
    }, [loadStatus])

    const selectedCmeId: string = useMemo(
        (): string => String(reportFilters.cme?.value ?? userInfo?.Scope?.CMEnvironmentID ?? DEFAULT_CME_ID),
        [reportFilters.cme, userInfo]
    )

    const selectedTelSysId: string = useMemo((): string => {
        const telSysValue: Nullable<string | number> = reportFilters.telephonyServer?.value ?? null

        return isAllTelSysSelection(telSysValue) ? String(ALL_VALUE) : String(telSysValue)
    }, [reportFilters.telephonyServer])

    const selectedPartitionId: string = useMemo((): string => {
        const partitionValue: Nullable<string | number> = reportFilters.partition?.value ?? null

        if (isAllTelSysSelection(reportFilters.telephonyServer?.value ?? null)) {
            return String(ALL_VALUE)
        }

        return IsVarEmpty(partitionValue) ? String(ALL_VALUE) : String(partitionValue)
    }, [reportFilters.partition, reportFilters.telephonyServer])

    const isFullAccessEnabled: boolean = status?.Data?.IsFullAccessEnabled === true
    const billingExclusionsConfigured: boolean = status?.Data?.BillingExclusionsConfigured === true
    const canReload: boolean = hasPermission(userInfo, Permissions.LICENSE_UPDATE) && isFullAccessEnabled
    const canDownloadBillingSummary: boolean = hasPermission(userInfo, Permissions.BILLING_SUMMARY)
    const canDownloadBillingCdr: boolean = hasPermission(userInfo, Permissions.BILLING_CDRs)
    const canAccessBillingReports: boolean = canDownloadBillingSummary || canDownloadBillingCdr
    const canIncludeSystemWideUsers: boolean =
        selectedCmeId === DEFAULT_CME_ID && isAllTelSysSelection(reportFilters.telephonyServer?.value ?? null)

    useEffect((): void => {
        if (!canIncludeSystemWideUsers && includeSystemWideUsers) {
            setIncludeSystemWideUsers(false)
        }
    }, [canIncludeSystemWideUsers, includeSystemWideUsers])

    useEffect((): void => {
        if (!billingExclusionsConfigured && includeBillingExcludedConfigurations) {
            setIncludeBillingExcludedConfigurations(false)
        }
    }, [billingExclusionsConfigured, includeBillingExcludedConfigurations])

    const statusColumns: readonly DataTableColumn<LicenseStatusRow, keyof LicenseStatusRow>[] = useMemo(
        (): readonly DataTableColumn<LicenseStatusRow, keyof LicenseStatusRow>[] => [
            {
                field: 'Description',
                key: 'license-management-column-description',
                headerName: language.get(LicenseManagementLanguage.LicenseManagement_Status_Entry),
                sortable: false,
                align: 'left'
            },
            {
                field: 'value',
                key: 'license-management-column-value',
                headerName: language.get(LicenseManagementLanguage.LicenseManagement_Status_Value),
                sortable: false,
                align: 'left'
            },
            {
                field: 'usage',
                key: 'license-management-column-usage',
                headerName: language.get(LicenseManagementLanguage.LicenseManagement_Status_Usage),
                sortable: false,
                align: 'left'
            },
            {
                field: 'statusMessage',
                key: 'license-management-column-status',
                headerName: language.get(LicenseManagementLanguage.LicenseManagement_Status_Status),
                sortable: false,
                align: 'left'
            }
        ],
        [language]
    )

    const statusRows: readonly LicenseStatusRow[] = useMemo(
        (): readonly LicenseStatusRow[] =>
            (status?.Data?.Items ?? []).map(
                (item: LicenseStatusItem, index: number): LicenseStatusRow =>
                    mapStatusItemToRow(item, `license-status-${String(index)}`)
            ),
        [status]
    )

    const profileSections: readonly DataTableSection<LicenseStatusRow>[] = useMemo(
        (): readonly DataTableSection<LicenseStatusRow>[] =>
            (status?.Data?.TelSysProfiles ?? []).map(
                (profile: LicenseTelSysProfile): DataTableSection<LicenseStatusRow> => ({
                    key: profile.LicenseItemID,
                    title: profile.LicenseItemName,
                    rows: (profile.LicenseValues ?? []).map(
                        (item: LicenseStatusItem, index: number): LicenseStatusRow =>
                            mapStatusItemToRow(item, `${profile.LicenseItemID}-${String(index)}`)
                    )
                })
            ),
        [status]
    )

    const refreshStatus = useCallback((): void => {
        loadStatus()
    }, [loadStatus])

    const reloadStatus = useCallback((): void => {
        displayPromptPromise(language.get(LicenseManagementLanguage.LicenseManagement_Reload_Confirm))
            .then((): void => {
                setIsReloading(true)

                reloadLicenseManagementStatus()
                    .then((): void => {
                        displaySuccessMessage(language.get(LicenseManagementLanguage.LicenseManagement_Reload_Success))
                        loadStatus()
                    })
                    .catch(displayError)
                    .finally((): void => {
                        if (isMounted.current) setIsReloading(false)
                    })
            })
            .catch((): void => undefined)
    }, [displayPromptPromise, language, loadStatus])

    const openReport = useCallback(
        (reportType: LicenseReportType, confirmMessage: string): void => {
            displayPromptPromise(confirmMessage)
                .then((): void => {
                    const reportWindow: Nullable<Window> = window.open(
                        getLicenseManagementReportDownloadURL(reportType, {
                            CMEnvironmentID: selectedCmeId,
                            telSysID: selectedTelSysId,
                            partitionIDInTelSys: selectedPartitionId,
                            onlyBillableConfiguration,
                            includeSystemWideUsers,
                            includeBillingExcludedConfigurations,
                            includeSimulationTelephoneSystems,
                            includeNonBillingExcludedConfigurations: true
                        }).constructURL(),
                        '_blank',
                        'noopener,noreferrer'
                    )

                    reportWindow?.focus()
                })
                .catch((): void => undefined)
        },
        [
            displayPromptPromise,
            includeBillingExcludedConfigurations,
            includeSimulationTelephoneSystems,
            includeSystemWideUsers,
            onlyBillableConfiguration,
            selectedCmeId,
            selectedPartitionId,
            selectedTelSysId
        ]
    )

    return (
        <div className='license-management-page-container'>
            <div className='license-management-page admin-list-container'>
                <div className='license-management-page-title-row'>
                    <PageTitle title={RouterLang.PageTitle_licences} />
                    <div className='license-management-page-title-actions'>
                        <IconButton
                            customClassName='license-management-refresh-button'
                            iconName={IconName.REFRESH}
                            type={IconButtonType.BUTTON}
                            title={language.get(LicenseManagementLanguage.LicenseManagement_Action_Refresh)}
                            onClick={refreshStatus}
                            isEnabled={!isLoading && !isReloading}
                        />
                        {canReload && (
                            <IconButton
                                customClassName='license-management-reload-button'
                                iconName={IconName.REPLAY}
                                type={IconButtonType.BUTTON}
                                title={language.get(LicenseManagementLanguage.LicenseManagement_Action_Reload)}
                                onClick={reloadStatus}
                                isEnabled={!isLoading && !isReloading}
                            />
                        )}
                    </div>
                </div>
                <div className='license-management-page-content'>
                    <div className={LICENSE_MANAGEMENT_PANEL_SECTION_CLASS}>
                        <h2 className={LICENSE_MANAGEMENT_STATUS_TITLE_CLASS}>
                            {language.get(LicenseManagementLanguage.LicenseManagement_Status_Title)}
                        </h2>
                        <DataTable<LicenseStatusRow, keyof LicenseStatusRow>
                            columns={[...statusColumns]}
                            data={[...statusRows]}
                            dense
                            toolbarDisabled
                            paginationDisabled
                            isLoading={isLoading}
                            localeText={{
                                noRows: language.get(LicenseManagementLanguage.LicenseManagement_Status_Empty)
                            }}
                        />
                        {!isLoading &&
                            !isFullAccessEnabled &&
                            isStringNotEmpty(status?.Data?.Items?.[0]?.Description ?? EMPTY_STRING) && (
                                <div className='license-management-limited-access'>
                                    {language.get(LicenseManagementLanguage.LicenseManagement_Status_LimitedAccess)}
                                </div>
                            )}
                    </div>
                    {isFullAccessEnabled && (
                        <div className='license-management-profiles-section'>
                            <h2 className={LICENSE_MANAGEMENT_STATUS_TITLE_CLASS}>
                                {language.get(LicenseManagementLanguage.LicenseManagement_Profiles_Title)}
                            </h2>
                            <DataTable<LicenseStatusRow, keyof LicenseStatusRow>
                                columns={[...statusColumns]}
                                sections={profileSections}
                                dense
                                toolbarDisabled
                                paginationDisabled
                                localeText={{
                                    noRows: language.get(
                                        LicenseManagementLanguage.LicenseManagement_Profiles_Empty
                                    )
                                }}
                            />
                        </div>
                    )}
                    {canAccessBillingReports && (
                        <div className='license-management-billing-section'>
                            <h2 className={LICENSE_MANAGEMENT_STATUS_TITLE_CLASS}>
                                {language.get(LicenseManagementLanguage.LicenseManagement_Billing_Title)}
                            </h2>
                            <div className='license-management-billing-panel'>
                                <div className={LICENSE_MANAGEMENT_PANEL_SECTION_CLASS}>
                                    <h3>{language.get(LicenseManagementLanguage.LicenseManagement_Billing_Filters)}</h3>
                                    <AdminFilters
                                        optionsToDisplay={{
                                            cme: true,
                                            telephonyServer: true,
                                            partition: true
                                        }}
                                        localeText={{
                                            cmeLabel: language.get(
                                                LicenseManagementLanguage.LicenseManagement_Filter_Cme
                                            ),
                                            telephonyServerLabel: language.get(
                                                LicenseManagementLanguage.LicenseManagement_Filter_TelSys
                                            ),
                                            partitionLabel: language.get(
                                                LicenseManagementLanguage.LicenseManagement_Filter_Partition
                                            )
                                        }}
                                        onChange={setReportFilters}
                                    />
                                </div>
                                <div className={LICENSE_MANAGEMENT_PANEL_SECTION_CLASS}>
                                    <h3 className='license-management-advanced-title'>
                                        {language.get(LicenseManagementLanguage.LicenseManagement_Billing_Advanced)}
                                    </h3>
                                    <div className='license-management-checkbox-grid'>
                                        <RouterCheckbox
                                            isChecked={onlyBillableConfiguration}
                                            onChange={setOnlyBillableConfiguration}
                                            labelRight={language.get(
                                                LicenseManagementLanguage.LicenseManagement_Billing_OnlyBillable
                                            )}
                                        />
                                        <RouterCheckbox
                                            isChecked={includeSystemWideUsers}
                                            disabled={!canIncludeSystemWideUsers}
                                            onChange={setIncludeSystemWideUsers}
                                            labelRight={language.get(
                                                LicenseManagementLanguage.LicenseManagement_Billing_SystemWideUsers
                                            )}
                                        />
                                        <RouterCheckbox
                                            isChecked={includeBillingExcludedConfigurations}
                                            disabled={!billingExclusionsConfigured}
                                            onChange={setIncludeBillingExcludedConfigurations}
                                            labelRight={language.get(
                                                LicenseManagementLanguage.LicenseManagement_Billing_BillingExcluded
                                            )}
                                        />
                                        <RouterCheckbox
                                            isChecked={includeSimulationTelephoneSystems}
                                            onChange={setIncludeSimulationTelephoneSystems}
                                            labelRight={language.get(
                                                LicenseManagementLanguage.LicenseManagement_Billing_Simulation
                                            )}
                                        />
                                    </div>
                                </div>
                                <div className={LICENSE_MANAGEMENT_PANEL_SECTION_CLASS}>
                                    <div className='license-management-report-actions'>
                                        {canDownloadBillingSummary && (
                                            <RoundedButton
                                                onClick={(): void =>
                                                    openReport(
                                                        LICENSE_REPORT_BILLING_SUMMARY,
                                                        language.get(
                                                            LicenseManagementLanguage.LicenseManagement_Billing_SummaryConfirm
                                                        )
                                                    )
                                                }
                                            >
                                                {language.get(
                                                    LicenseManagementLanguage.LicenseManagement_Billing_Summary
                                                )}
                                            </RoundedButton>
                                        )}
                                        {canDownloadBillingCdr && (
                                            <RoundedButton
                                                onClick={(): void =>
                                                    openReport(
                                                        LICENSE_REPORT_BILLING_CDR,
                                                        language.get(
                                                            LicenseManagementLanguage.LicenseManagement_Billing_CdrConfirm
                                                        )
                                                    )
                                                }
                                            >
                                                {language.get(LicenseManagementLanguage.LicenseManagement_Billing_Cdr)}
                                            </RoundedButton>
                                        )}
                                        {isFullAccessEnabled && (
                                            <RoundedButton
                                                isOutlined
                                                onClick={(): void =>
                                                    openReport(
                                                        LICENSE_REPORT_DIAGNOSTIC,
                                                        language.get(
                                                            LicenseManagementLanguage.LicenseManagement_Diagnostic_Confirm
                                                        )
                                                    )
                                                }
                                            >
                                                {language.get(LicenseManagementLanguage.LicenseManagement_Diagnostic)}
                                            </RoundedButton>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
